import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  GraduationCap,
  ArrowRight,
  Users,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  FileText,
  Loader2,
  BarChart3,
  Target,
  Award,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  User,
} from 'lucide-react';
import { toast } from 'sonner';

const ExamAnalytics = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { token, isTeacher } = useAuth();
  
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStudents, setExpandedStudents] = useState({});
  const toggleStudent = (sid) => setExpandedStudents(prev => ({ ...prev, [sid]: !prev[sid] }));

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isTeacher) {
      navigate('/teacher/login');
      return;
    }
    fetchAnalytics();
  }, [isTeacher, examId]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/teacher/analytics/exam/${examId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        toast.error('فشل تحميل التحليلات');
        navigate('/teacher/dashboard');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('حدث خطأ');
    }
    setLoading(false);
  };

  const getGradeColor = (grade) => {
    const colors = {
      A: 'bg-green-500',
      B: 'bg-blue-500',
      C: 'bg-yellow-500',
      D: 'bg-orange-500',
      F: 'bg-red-500',
    };
    return colors[grade] || 'bg-gray-500';
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-blue-600';
    if (rate >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#EF9F27] mx-auto mb-4" />
          <p className="text-[#6B4C2A]">جاري تحميل التحليلات...</p>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const totalGraded = Object.values(analytics.grade_distribution).reduce((a, b) => a + b, 0);
  const sortedQuestions = [...analytics.question_stats]
    .filter(q => q.total_answered > 0)
    .map(q => ({ ...q, wrong_count: q.total_answered - q.correct_count }))
    .sort((a, b) => b.wrong_count - a.wrong_count);
  const hardestQuestions = sortedQuestions.filter(q => q.wrong_count > 0).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="bg-[#FDF8F0] border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/teacher/dashboard" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#E8DFCF]" />
              <span className="text-xl font-bold text-[#412402]">المعتمد في اللغة العربية</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/teacher/dashboard')}
            className="text-[#6B4C2A] hover:text-[#412402] mb-4"
          >
            <ArrowRight className="w-4 h-4 ml-2" />
            العودة للوحة التحكم
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-purple-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#412402]">تحليلات الاختبار</h1>
              <p className="text-[#6B4C2A]">{analytics.exam_title}</p>
            </div>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8B7355]">إجمالي الطلاب</p>
                  <p className="text-3xl font-bold text-[#412402]">{analytics.total_students}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8B7355]">متوسط الدرجات</p>
                  <p className="text-3xl font-bold text-[#412402]">{analytics.average_score}</p>
                  <p className="text-xs text-[#8B7355]">من {analytics.total_points}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-purple-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8B7355]">نسبة النجاح</p>
                  <p className={`text-3xl font-bold ${getSuccessRateColor(analytics.pass_rate)}`}>
                    {analytics.pass_rate}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8B7355]">أعلى / أدنى</p>
                  <p className="text-lg font-bold text-green-600">{analytics.max_score}</p>
                  <p className="text-lg font-bold text-red-600">{analytics.min_score}</p>
                </div>
                <div className="w-12 h-12 bg-[#EF9F27]/15 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#EF9F27]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Grade Distribution */}
          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-[#412402]">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                توزيع التقديرات
              </CardTitle>
            </CardHeader>
            <CardContent>
              {totalGraded === 0 ? (
                <p className="text-[#8B7355] text-center py-4">لا توجد بيانات كافية</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(analytics.grade_distribution).map(([grade, count]) => {
                    const percentage = totalGraded > 0 ? (count / totalGraded) * 100 : 0;
                    return (
                      <div key={grade} className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium flex items-center gap-2 text-[#412402]">
                            <span className={`w-3 h-3 rounded-full ${getGradeColor(grade)}`}></span>
                            {grade === 'A' && 'ممتاز (90%+)'}
                            {grade === 'B' && 'جيد جداً (80-89%)'}
                            {grade === 'C' && 'جيد (70-79%)'}
                            {grade === 'D' && 'مقبول (50-69%)'}
                            {grade === 'F' && 'راسب (<50%)'}
                          </span>
                          <span className="text-[#6B4C2A]">{count} طالب ({percentage.toFixed(0)}%)</span>
                        </div>
                        <div className="h-4 bg-[#FFFFFF] rounded-full overflow-hidden border border-[#E8DFCF]">
                          <div 
                            className={`h-full ${getGradeColor(grade)} transition-all duration-500`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Hardest Questions */}
          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-[#412402]">
                <AlertTriangle className="w-5 h-5 text-[#EF9F27]" />
                الأسئلة الأصعب
              </CardTitle>
              <CardDescription className="text-[#8B7355]">الأسئلة التي واجه الطلاب صعوبة فيها</CardDescription>
            </CardHeader>
            <CardContent>
              {hardestQuestions.length === 0 ? (
                <div className="py-6 text-center">
                  <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                  <p className="text-[#8B7355] font-medium">لا يوجد سؤال صعب الى الان</p>
                  <p className="text-xs text-[#8B7355]/70 mt-1">لم يتم تسجيل إجابات خاطئة بعد</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {hardestQuestions.map((q, index) => (
                    <div key={q.question_id} className="p-4 bg-[#FFFFFF] rounded-lg border border-[#E8DFCF]">
                      <div className="flex items-start gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-[#412402] font-bold ${
                          index === 0 ? 'bg-red-500' : index === 1 ? 'bg-orange-500' : 'bg-yellow-500'
                        }`}>
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <p className="font-medium text-[#412402] text-sm mb-1 leading-relaxed">
                            {q.question_text}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-[#6B4C2A]">
                            <span>نسبة النجاح: <strong className={getSuccessRateColor(q.success_rate)}>{q.success_rate.toFixed(0)}%</strong></span>
                            <span>الإجابات: {q.total_answered}</span>
                          </div>
                          {q.most_common_wrong && (
                            <p className="text-xs text-red-600 mt-2">
                              الإجابة الخاطئة الأكثر شيوعاً: "{q.most_common_wrong}"
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Question-by-Question Analysis */}
        <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-[#412402]">
              <FileText className="w-5 h-5 text-blue-600" />
              تحليل الأسئلة
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.question_stats.length === 0 ? (
              <p className="text-[#8B7355] text-center py-8">لا توجد إجابات بعد</p>
            ) : (
              <div className="space-y-4">
                {analytics.question_stats.map((q, index) => (
                  <div 
                    key={q.question_id} 
                    className="p-4 bg-[#FFFFFF] border border-[#E8DFCF] rounded-lg hover:border-[#EF9F27]/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs border-[#E8DFCF] text-[#6B4C2A]">
                            سؤال {index + 1}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {q.question_type === 'multiple_choice' && 'اختيار من متعدد'}
                            {q.question_type === 'true_false' && 'صح/خطأ'}
                            {q.question_type === 'short_answer' && 'إجابة قصيرة'}
                            {q.question_type === 'paragraph' && 'مقالي'}
                          </Badge>
                        </div>
                        <p className="text-[#412402] text-sm mb-3 leading-relaxed whitespace-pre-wrap">{q.question_text}</p>
                        
                        {/* Answer Distribution Bars */}
                        {(q.question_type === 'multiple_choice' || q.question_type === 'true_false') && Object.keys(q.answer_distribution).length > 0 && (
                          <div className="space-y-3 mt-4">
                            <p className="text-xs text-[#8B7355] font-medium">توزيع الإجابات:</p>
                            {Object.entries(q.answer_distribution)
                              .sort((a, b) => b[1] - a[1])
                              .map(([answer, count]) => {
                                const percentage = q.total_answered > 0 ? (count / q.total_answered) * 100 : 0;
                                const isCorrect = answer === q.correct_answer;
                                return (
                                  <div key={answer} className="space-y-1">
                                    <div className="flex items-center justify-between gap-2">
                                      <span className={`text-sm leading-snug flex-1 ${isCorrect ? 'text-green-700 font-semibold' : 'text-[#6B4C2A]'}`}>
                                        {isCorrect ? '✓ ' : '○ '}{answer}
                                      </span>
                                      <span className={`text-xs font-medium whitespace-nowrap ${isCorrect ? 'text-green-600' : 'text-[#8B7355]'}`}>
                                        {count} طالب ({percentage.toFixed(0)}%)
                                      </span>
                                    </div>
                                    <div className="h-3 bg-[#FDF8F0] rounded-full overflow-hidden border border-[#E8DFCF]">
                                      <div
                                        className={`h-full transition-all duration-300 rounded-full ${isCorrect ? 'bg-green-500' : 'bg-[#d4a853]/40'}`}
                                        style={{ width: `${percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                      
                      {/* Success Rate Circle */}
                      <div className="text-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center border-4 ${
                          q.success_rate >= 70 ? 'border-green-500 text-green-600' :
                          q.success_rate >= 50 ? 'border-yellow-500 text-yellow-600' :
                          'border-red-500 text-red-600'
                        }`}>
                          <span className="text-lg font-bold">{q.success_rate.toFixed(0)}%</span>
                        </div>
                        <p className="text-xs text-[#8B7355] mt-1">نسبة النجاح</p>
                        <p className="text-xs text-[#8B7355]">{q.correct_count}/{q.total_answered}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        {/* Student Answers */}
        {analytics.student_results && analytics.student_results.length > 0 && (
          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-[#412402]">
                <Users className="w-5 h-5 text-indigo-600" />
                إجابات الطلاب
              </CardTitle>
              <CardDescription className="text-[#8B7355]">اضغط على اسم الطالب لعرض إجاباته التفصيلية</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics.student_results.map((sr) => (
                  <div key={sr.student_id} className="border border-[#E8DFCF] rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between p-4 bg-[#FFFFFF] hover:bg-[#FDF8F0] transition-colors"
                      onClick={() => toggleStudent(sr.student_id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center shrink-0">
                          <User className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-[#412402]">{sr.student_name}</p>
                          <p className="text-xs text-[#8B7355]">{sr.grade} - شعبة {sr.section}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-lg font-bold text-[#EF9F27]">{sr.score}</p>
                          <p className="text-xs text-[#8B7355]">من {analytics.total_points}</p>
                        </div>
                        <Badge
                          className={sr.status === 'terminated' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}
                        >
                          {sr.status === 'terminated' ? 'مخالفة' : 'مكتمل'}
                        </Badge>
                        {expandedStudents[sr.student_id] ? <ChevronUp className="w-4 h-4 text-[#412402]/50" /> : <ChevronDown className="w-4 h-4 text-[#412402]/50" />}
                      </div>
                    </button>
                    {expandedStudents[sr.student_id] && (
                      <div className="border-t border-[#E8DFCF] divide-y divide-[#F5EAD3]">
                        {sr.answers.map((ans, i) => (
                          <div key={ans.question_id} className="p-3 flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full bg-[#EF9F27]/15 flex items-center justify-center text-xs font-bold text-[#EF9F27] shrink-0 mt-0.5">{i + 1}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#412402] mb-1 leading-snug">{ans.question_text}</p>
                              <div className="flex flex-wrap gap-2 text-xs">
                                <span className={`px-2 py-0.5 rounded-full font-medium ${
                                  ans.is_correct === true ? 'bg-green-100 text-green-700' :
                                  ans.is_correct === false ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {ans.student_answer ?? 'لم يجب'}
                                </span>
                                {ans.is_correct === false && ans.correct_answer && (
                                  <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                                    الصحيح: {ans.correct_answer}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="shrink-0">
                              {ans.is_correct === true && <CheckCircle className="w-4 h-4 text-green-500" />}
                              {ans.is_correct === false && <XCircle className="w-4 h-4 text-red-500" />}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ExamAnalytics;
